import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import * as React from 'react';
import { useId } from 'react-id-generator';
import { useControlledDropdown } from '../../hooks/use_controlled_dropdown.js';
import { bindRefs } from '../../utils/react.js';
import { Popper } from '../Popper/Popper.js';
import { POPOVER_ERROR_MESSAGES } from './constants.js';
import { PopoverDialog } from './PopoverDialog/PopoverDialog.js';
import { PopoverTrigger } from './PopoverTrigger/PopoverTrigger.js';
import { jsx } from 'react/jsx-runtime';

var Popover = function Popover(_ref) {
  var className = _ref.className,
    content = _ref.content,
    CTAs = _ref.CTAs,
    _ref$dismissOnOutside = _ref.dismissOnOutsideClick,
    dismissOnOutsideClick = _ref$dismissOnOutside === void 0 ? true : _ref$dismissOnOutside,
    header = _ref.header,
    hideDismissButton = _ref.hideDismissButton,
    inline = _ref.inline,
    onHide = _ref.onHide,
    onOutsideClick = _ref.onOutsideClick,
    onShow = _ref.onShow,
    _ref$placement = _ref.placement,
    placement = _ref$placement === void 0 ? "bottom-start" : _ref$placement,
    popoverAriaLabel = _ref.popoverAriaLabel,
    showing = _ref.showing,
    style = _ref.style,
    tertiaryAction = _ref.tertiaryAction,
    trigger = _ref.trigger;
  var _useControlledDropdow = useControlledDropdown({
      showing: showing
    }),
    setShowingState = _useControlledDropdow.setShowingState,
    showingState = _useControlledDropdow.showingState;
  var _useId = useId(1, "popover-"),
    _useId2 = _slicedToArray(_useId, 1),
    popoverId = _useId2[0];
  var dialogRef = React.useRef(null);
  var hidePopover = React.useCallback(function () {
    setShowingState(false);
    if (onHide) {
      onHide();
    }
  }, [onHide, setShowingState]);
  var showPopover = React.useCallback(function () {
    setShowingState(true);
    if (onShow) {
      onShow();
    }
  }, [onShow, setShowingState]);
  var handleTogglePopover = React.useCallback(function () {
    if (showingState) {
      hidePopover();
    } else {
      showPopover();
    }
  }, [hidePopover, showPopover, showingState]);
  var handleOutsideClick = React.useCallback(function (e) {
    if (dismissOnOutsideClick) {
      hidePopover();
    }
    if (onOutsideClick) {
      onOutsideClick(e);
    }
  }, [dismissOnOutsideClick, hidePopover, onOutsideClick]);
  if (typeof header !== "string" && !popoverAriaLabel) {
    console.error(POPOVER_ERROR_MESSAGES.POPOVER_REQUIRES_ARIA_WITHOUT_STRING_HEADER);
  }
  if (hideDismissButton && !dismissOnOutsideClick) {
    console.error(POPOVER_ERROR_MESSAGES.POPOVER_REQUIRES_DISMISS_BUTTON_OR_DISMISS_OUTSIDE_CLICK);
  }
  return /*#__PURE__*/jsx(Popper, {
    inline: inline,
    offset: [0, 4],
    onOutsideClick: handleOutsideClick,
    placement: placement,
    onShow: function onShow() {
      var _dialogRef$current;
      return (_dialogRef$current = dialogRef.current) === null || _dialogRef$current === void 0 ? void 0 : _dialogRef$current.focus();
    },
    renderContent: function renderContent(_ref2) {
      var contentRef = _ref2.ref;
      return /*#__PURE__*/jsx(PopoverDialog, {
        ariaLabel: popoverAriaLabel,
        className: className,
        content: content,
        CTAs: CTAs,
        header: header,
        hideDismissButton: hideDismissButton,
        hidePopover: hidePopover,
        popoverId: popoverId,
        ref: bindRefs(dialogRef, contentRef),
        style: style,
        tertiaryAction: tertiaryAction
      });
    },
    renderTrigger: function renderTrigger(_ref3) {
      var triggerRef = _ref3.ref;
      return /*#__PURE__*/jsx(PopoverTrigger, {
        popoverId: popoverId,
        ref: triggerRef,
        showing: showingState,
        togglePopover: handleTogglePopover,
        trigger: trigger
      });
    },
    showing: showingState
  });
};
Popover.displayName = "Popover";

export { Popover };
